import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router';
import {
  DEVELOPER_TOOLS_VIEWS,
  ECONOMICS_VIEWS,
  FARM_DATA_VIEWS,
  MULTI_PROJECT_DASHBOARD_VIEW,
  READ_ONLY_DEVELOPER_TOOLS_VIEWS,
} from '../../constants/views';
import { SUPERADMIN_VIEWS } from '../../constants/superadminViews';
import CheckIcon from '../icons/CheckIcon';
import SidePanel, {
  PanelLink,
  PanelMainGroup,
  PanelSection,
  PanelTab,
} from '../SidePanel';
import LayersTree from './LayersTree';
import FieldsTree from './FieldsTree';
import TeamsTree from './TeamsTree';
import { IconSpan, NavMainItem, StickyHeader } from './menu-components';
import {
  useIsFarmView,
  useIsMultiProjectDashboardView,
  useUserCanEditFarm,
  useUserRole,
} from '../../utils/hooks';
import GlobeIcon from '../icons/GlobeIcon';
import ProjectionIcon from '../icons/ProjectionIcon';
import FarmDataIcon from '../icons/FarmDataIcon';
import { getFarmPath } from '../../utils/utils';
import ChevronIcon from '../icons/ChevronIcon';
import SectionGroupsMenu from './SectionGroupsMenu';
import DeveloperToolsIcon from '../icons/DeveloperToolsIcon';
import { useFarmSourceContext } from '../../contexts/FarmSourceContext';
import DashboardLeftMenu from '../multi-project-dashboard/DashboardLeftMenu';
import { HOME_ROUTES } from '../../routes/HomeRoutes';

// Helper for ViewsPanel and ViewsPanelWithHeader
const ViewsPanelItemsList = ({ views, linkPrefix }) => {
  const location = useLocation();
  return (
    <ul>
      {views.map((view) => {
        const { name, path, onClick } = view;
        const fullPath = path.startsWith('/') ? path : `/${path}`;
        const isActive = location.pathname.startsWith(
          `${linkPrefix}${fullPath}`,
        );
        return (
          <NavMainItem key={view.path} isActive={isActive}>
            <PanelLink
              to={`${linkPrefix}${fullPath}`}
              isActive={isActive}
              onClick={onClick}
            >
              <IconSpan>{isActive && <CheckIcon />}</IconSpan>
              {name}
            </PanelLink>
          </NavMainItem>
        );
      })}
    </ul>
  );
};

const ViewsPanelWithHeader = ({
  views,
  linkPrefix,
  header,
  icon,
  startCollapsed,
  collapsible = false,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(startCollapsed);
  return (
    <PanelSection>
      <StickyHeader
        className="d-flex align-items-center justify-content-between"
        onClick={() => collapsible && setIsCollapsed(!isCollapsed)}
        {...(collapsible && { role: 'button' })}
      >
        <div className="d-flex">
          <IconSpan>{icon}</IconSpan>
          {header}
        </div>
        {collapsible && (
          <IconSpan>
            <ChevronIcon direction={isCollapsed ? 'down' : 'up'} />
          </IconSpan>
        )}
      </StickyHeader>
      {(!collapsible || !isCollapsed) && (
        <ViewsPanelItemsList views={views} linkPrefix={linkPrefix} />
      )}
    </PanelSection>
  );
};

const ViewsPanel = ({ views, linkPrefix }) => {
  return (
    <PanelSection>
      <ViewsPanelItemsList views={views} linkPrefix={linkPrefix} />
    </PanelSection>
  );
};

const FarmSideNav = () => {
  const { farmId } = useParams();
  const farmPath = getFarmPath(farmId, 'fields');
  const location = useLocation();
  const userCanEditFarm = useUserCanEditFarm(farmId);
  const { isSuperadmin } = useUserRole();

  const isFieldsView =
    location.pathname.startsWith(farmPath) ||
    /^\/app\/?$/.test(location.pathname);

  const [activeTab, setActiveTab] = useState('');

  useEffect(() => setActiveTab(isFieldsView ? 'fields' : 'economics'), [
    isFieldsView,
  ]);

  return (
    <SidePanel>
      <PanelSection>
        <ul className="d-flex">
          <li>
            <PanelLink
              to={farmPath}
              isActive={activeTab === 'fields'}
              onClick={() => setActiveTab('fields')}
            >
              Fields
            </PanelLink>
          </li>
          <li>
            <PanelTab
              role="button"
              isActive={activeTab === 'economics'}
              onClick={() => setActiveTab('economics')}
            >
              Economics
            </PanelTab>
          </li>
        </ul>
      </PanelSection>
      <PanelMainGroup>
        {activeTab === 'fields' && (
          <>
            <SectionGroupsMenu />
            <FieldsTree />
            <LayersTree />
          </>
        )}
        {activeTab === 'economics' && (
          <>
            <ViewsPanelWithHeader
              views={ECONOMICS_VIEWS}
              linkPrefix={`/app/farm/${farmId}`}
              header="Projections"
              icon={<ProjectionIcon color="#54A400" />}
            />
            {userCanEditFarm && (
              <ViewsPanelWithHeader
                views={FARM_DATA_VIEWS}
                linkPrefix={getFarmPath(farmId, 'data')}
                header="Farm Data"
                icon={<FarmDataIcon />}
              />
            )}
            {isSuperadmin && (
              // start collapsed so that developer tools are hidden on navigation away and back
              <ViewsPanelWithHeader
                views={
                  userCanEditFarm
                    ? DEVELOPER_TOOLS_VIEWS
                    : READ_ONLY_DEVELOPER_TOOLS_VIEWS
                }
                linkPrefix={getFarmPath(farmId, 'developer-tools')}
                header="Developer Tools"
                icon={<DeveloperToolsIcon />}
                collapsible
                startCollapsed
              />
            )}
          </>
        )}
      </PanelMainGroup>
    </SidePanel>
  );
};

const AppSideNav = () => {
  const { isSuperadmin } = useUserRole();
  const { setSource } = useFarmSourceContext();
  const { pathname } = useLocation();

  return (
    <SidePanel>
      <PanelMainGroup>
        <ViewsPanel views={HOME_ROUTES} linkPrefix="/app" />
        <ViewsPanel
          views={[
            {
              ...MULTI_PROJECT_DASHBOARD_VIEW,
              // set source so that back button on dashboard view
              // goes back to the previous page
              onClick: () => setSource(pathname),
            },
          ]}
          linkPrefix="/app"
        />
        <TeamsTree />
        {isSuperadmin && (
          <ViewsPanelWithHeader
            views={SUPERADMIN_VIEWS}
            linkPrefix="/superadmin"
            header="Global Data"
            icon={<GlobeIcon />}
          />
        )}
      </PanelMainGroup>
    </SidePanel>
  );
};

const SideNavigation = () => {
  const isFarmView = useIsFarmView();
  const isMultiProjectDashboardView = useIsMultiProjectDashboardView();

  return isFarmView ? (
    <FarmSideNav />
  ) : isMultiProjectDashboardView ? (
    <DashboardLeftMenu />
  ) : (
    <AppSideNav />
  );
};

export default SideNavigation;
